import React, { useEffect, useState } from "react";
import { Button, Space, Popover } from "antd";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ExclamationCircleFilled, InfoCircleFilled, QuestionCircleFilled } from "@ant-design/icons";

import * as Common from "../commons/common";
import * as Utils from "../utils/utils";

import CustomSheet from "./CustomSheet";
import IconEvStation from "../assets/images/mypage/EV-station-icon.svg";
import IconCsCenter from "../assets/images/mypage/CS-icon.svg";
import IconCsManual from "../assets/images/mypage/manual-icon.svg";
import IconEmergency from "../assets/images/mypage/emergencyIcon.svg";
import dash from "../assets/images/dash-image.svg";
import styles from "./AddOnsDrawer.module.css";

const btnAnim = {
    initial: { backgroundColor: `#fff` },
    animate: {},
    whileTap: { scale: 0.9 },
};

declare const window: typeof globalThis & {
    webkit: any;
};

function AddOnsDrawer({ openDrawer, setOpenDrawer }: { openDrawer: boolean; setOpenDrawer: (open: boolean) => void }) {
    const [openCsPopover, setOpenCsPopover] = useState(false);
    const [openEmergencyPopover, setOpenEmergencyPopover] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        openDrawer ? Utils.addDrawerStack() : Utils.popDrawerStack();
    }, [openDrawer]);

    const findEVStation = () => {
        Utils.popDrawerStack();
        navigate(Common.PAGE_FIND_EV_STATION);
    };

    return (
        <>
            <CustomSheet
                className={styles.drawer}
                isOpen={openDrawer}
                content={
                    <div className="space-page">
                        <div className={styles.csContainer}>
                            <div className={styles.csInnerContainer}>
                                <div className={styles.pageTitle}>
                                    <h1 className={styles.titleText}>고객편의</h1>
                                    <img height={10} width={56} src={dash} alt="dash design in yellow" className={styles.dash} />
                                </div>
                                <Space size={0} className={styles.csIconList}>
                                    <Button type="text" className={styles.button} onClick={findEVStation}>
                                        <div className={styles.iconWrapper}>
                                            <img width={38} height={42} src={IconEvStation} alt="ev station icon" className={styles.csIcon} />
                                            <span className={styles.csIconText}>충전소찾기</span>
                                        </div>
                                    </Button>
                                    <Popover
                                        content={
                                            <section className={styles.wrapper}>
                                                <div className={styles.pageTitle}>
                                                    <h1 className={styles.titleText}>공식홈페이지에서 확인하기</h1>
                                                    <img src={dash} height={10} width={56} alt="dash design in yellow" className={styles.dash} />
                                                </div>
                                                <motion.button
                                                    className={styles.motionBtn}
                                                    variants={btnAnim}
                                                    whileTap="whileTap"
                                                    onClick={() => {
                                                        window.open("https://ruta40.co.kr/information");
                                                        if (window.webkit) {
                                                            const message = {
                                                                url: "https://ruta40.co.kr/information",
                                                            };

                                                            window.webkit.messageHandlers.openUrl.postMessage(message);
                                                        }
                                                    }}
                                                >
                                                    <a className={styles.link}>
                                                        <InfoCircleFilled style={{ marginRight: "8px" }} />
                                                        주요 안내
                                                    </a>
                                                </motion.button>
                                                <motion.button
                                                    className={styles.motionBtn}
                                                    variants={btnAnim}
                                                    whileTap="whileTap"
                                                    onClick={() => {
                                                        window.open("https://ruta40.co.kr/faq");
                                                        if (window.webkit) {
                                                            const message = {
                                                                url: "https://ruta40.co.kr/faq",
                                                            };

                                                            window.webkit.messageHandlers.openUrl.postMessage(message);
                                                        }
                                                    }}
                                                >
                                                    <a className={styles.link}>
                                                        <QuestionCircleFilled style={{ marginRight: "8px" }} />
                                                        자주하는질문
                                                    </a>
                                                </motion.button>
                                            </section>
                                        }
                                        trigger="click"
                                        open={openCsPopover}
                                        onOpenChange={setOpenCsPopover}
                                    >
                                        <Button
                                            type="text"
                                            className={styles.button}
                                            onClick={() => {
                                                setOpenCsPopover(true);
                                            }}
                                        >
                                            <div className={styles.iconWrapper}>
                                                <img width={40} height={41} src={IconCsCenter} alt="customer center icon" className={styles.csIcon} />
                                                <span className={styles.csIconText}>고객지원</span>
                                            </div>
                                        </Button>
                                    </Popover>

                                    <Button
                                        type="text"
                                        className={styles.button}
                                        onClick={() => {
                                            window.open("https://ruta40.co.kr/downloadcenter");
                                            if (window.webkit) {
                                                const message = {
                                                    url: "https://ruta40.co.kr/downloadcenter",
                                                };

                                                window.webkit.messageHandlers.openUrl.postMessage(message);
                                            }
                                        }}
                                    >
                                        <div className={styles.iconWrapper}>
                                            <img width={34} height={42} src={IconCsManual} alt="manual icon" className={styles.csIcon} />
                                            <span className={styles.csIconText}>사용매뉴얼</span>
                                        </div>
                                    </Button>

                                    <Popover
                                        placement="top"
                                        content={
                                            <section className={styles.wrapper}>
                                                <div className={styles.pageTitle}>
                                                    <h1 className={styles.titleText}>AS 안내</h1>
                                                    <img src={dash} height={10} width={56} alt="dash design in yellow" className={styles.dash} />
                                                </div>
                                                <p style={{ marginBottom: 8 }}>
                                                    <span style={{ color: "var(--danger-color)" }}>긴급 상황</span>은 자체 AS 시스템으로 즉각 대응하고
                                                    있습니다.
                                                </p>
                                                <p style={{ marginBottom: 8 }}>
                                                    일반 AS 접수(증상/사진 첨부) 및 차량 문의는 아래로 접수해 주세요.
                                                    <br />
                                                    긴급 업무로 즉각 답변이 어려울 수 있으며, 접수 후 24시간 내 답변드리겠습니다.
                                                </p>
                                                <ul style={{ marginBottom: 0, paddingLeft: 24 }}>
                                                    <li style={{ fontWeight: "bold" }}>AS 담당자: 010-5918-1876</li>
                                                    <li style={{ fontWeight: "bold" }}>고객센터: 070-7707-1768</li>
                                                    <li className={styles.tdNone}>*착신 전환으로 연결 시간이 소요될 수 있음</li>
                                                </ul>
                                            </section>
                                        }
                                        trigger="click"
                                        open={openEmergencyPopover}
                                        onOpenChange={setOpenEmergencyPopover}
                                    >
                                        <Button
                                            type="text"
                                            className={styles.button}
                                            onClick={() => {
                                                setOpenEmergencyPopover(true);
                                            }}
                                        >
                                            <div className={styles.iconWrapper}>
                                                <img
                                                    width={40}
                                                    height={41}
                                                    src={IconEmergency}
                                                    alt="customer center icon"
                                                    className={styles.csIcon}
                                                />
                                                <span className={styles.csIconText}>AS안내</span>
                                            </div>
                                        </Button>
                                    </Popover>
                                </Space>
                            </div>
                        </div>
                    </div>
                }
                onClose={() => setOpenDrawer(false)}
            />
        </>
    );
}

export default AddOnsDrawer;
